/**
 * English strings.
 *
 * Plain shop words, not software words: "Exchange an item", never "Create return
 * transaction". Buttons say exactly what they do, and the message afterwards uses
 * the same words the button did.
 */
export const en = {
  // --- general -------------------------------------------------------------
  'app.name': 'POS',
  'app.brand': 'AppBay POS',
  'app.tagline': 'Till and stock',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.add': 'Add',
  'common.done': 'Done',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.search': 'Search',
  'till.left': '{n} left',
  'common.searchPlaceholder': 'Search by name, size or barcode',
  'common.loading': 'Loading',
  'common.none': 'None',
  'common.all': 'All',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.total': 'Total',
  'common.subtotal': 'Subtotal',
  'common.qty': 'Qty',
  'common.price': 'Price',
  'common.cost': 'Cost',
  'common.profit': 'Profit',
  'common.size': 'Size',
  'common.colour': 'Colour',
  'common.product': 'Product',
  'common.category': 'Category',
  'common.brand': 'Brand',
  'common.supplier': 'Supplier',
  'common.barcode': 'Barcode',
  'common.sku': 'Code',
  'common.stock': 'Stock',
  'common.date': 'Date',
  'common.time': 'Time',
  'common.name': 'Name',
  'common.nameEn': 'Name in English',
  'common.nameAr': 'Name in Arabic',
  'common.phone': 'Phone',
  'common.days': '{n} days',
  'common.note': 'Note',
  'common.notes': 'Notes',
  'common.reason': 'Reason',
  'common.print': 'Print',
  'common.export': 'Export to Excel',
  'common.exportPdf': 'Save as PDF',
  'common.confirm': 'Are you sure?',
  'common.required': 'This is needed',
  'common.optional': 'optional',
  'common.of': 'of',
  'common.items': 'items',
  'common.item': 'item',
  'common.customer': 'Customer',
  'common.cashier': 'Cashier',
  'common.receipt': 'Receipt',
  'common.apply': 'Apply',
  'common.clear': 'Clear',
  'common.retry': 'Try again',
  'common.language': 'Language',
  'common.today': 'Today',
  'common.yesterday': 'Yesterday',
  'common.thisWeek': 'This week',
  'common.thisMonth': 'This month',
  'common.lastMonth': 'Last month',
  'common.thisYear': 'This year',
  'common.custom': 'Choose dates',
  'common.from': 'From',
  'common.to': 'To',
  'common.filter': 'Filter',
  'common.noResults': 'Nothing found',
  'common.saved': 'Saved',
  'common.deleted': 'Deleted',
  'common.perPage': 'per page',
  'common.more': 'More',
  'common.menu': 'Menu',
  'common.signOut': 'Sign out',
  'common.discount': 'Discount',
  'common.vat': 'VAT',
  'common.change': 'Change',
  'common.paid': 'Paid',
  'common.due': 'Due',
  'common.amount': 'Amount',
  'common.currency': 'Currency',
  'common.rate': 'Rate',
  'common.percent': 'Percent',
  'common.fixed': 'Amount',
  'common.day': 'Day',
  'common.hour': 'Hour',

  // --- sign in --------------------------------------------------------------
  'login.title': 'Sign in',
  'login.username': 'Username',
  'login.password': 'Password',
  'login.signIn': 'Sign in',
  'login.wrong': 'That username or password is not right.',
  'login.tooMany': 'Too many attempts. Wait a moment and try again.',
  'login.welcome': 'Welcome back',
  'login.signInHint': 'Sign in with the username and password the owner gave you.',
  'login.offlineUnknown': 'No internet, and this person has not signed in on this device before. Connect once, then it will work offline.',
  'login.offlineTooOld': 'No internet, and it has been over a month since this person signed in here. Connect once to sign in.',
  'login.offlineSession': 'Signed in offline. Everything is saved on this device.',
  'login.remember': 'Remember me',
  'login.rememberHint': 'Stay signed in on this device for 30 days.',
  'login.account': 'Account code',
  'login.accountHint': 'The code your shop was given. It stays filled in on this till.',
  'login.unknownAccount': 'No shop has this account code.',
  'login.accountSuspended': "This shop's account is paused. Contact your provider.",
  'login.offlineOtherShop': 'No internet. Signing in to a different shop needs a connection.',
  'login.switchBlocked': 'This till still has {n} sale(s) waiting to upload. Connect to the internet and let them upload before signing in to another shop.',

  // --- navigation -----------------------------------------------------------
  'nav.checkout': 'Checkout',
  'nav.exchange': 'Exchange',
  'nav.priceCheck': 'Price check',
  'nav.shift': 'My shift',
  'nav.held': 'Held sales',
  'nav.dashboard': 'Today',
  'nav.products': 'Products',
  'nav.receive': 'Receive stock',
  'nav.adjust': 'Adjust stock',
  'nav.stockCount': 'Stock count',
  'nav.labels': 'Labels',
  'nav.lists': 'Lists',
  'nav.sales': 'Sales history',
  'nav.customers': 'Customers',
  'nav.owed': 'Money owed',
  'nav.bills': 'Supplier invoices',
  'nav.credits': 'Store credit',
  'nav.reports': 'Reports',
  'nav.expenses': 'Expenses',
  'nav.settings': 'Settings',
  'nav.users': 'Staff',
  'nav.devices': 'Tills',
  'nav.audit': 'Activity log',
  'nav.groupSell': 'Selling',
  'nav.groupStock': 'Stock',
  'nav.groupMoney': 'Money',
  'nav.groupSetup': 'Setup',
  'nav.hintDashboard': "Today's takings and what needs attention",
  'nav.hintCheckout': 'Ring up a sale',
  'nav.hintExchange': 'Take something back, or swap a size',
  'nav.hintPriceCheck': 'Scan an item to see its price and stock',
  'nav.hintShift': 'Open the drawer, count it, close the day',
  'nav.hintHeld': 'Sales you parked to finish later',
  'nav.hintProducts': 'Add clothes, set prices, see what is in stock',
  'nav.hintLabels': 'Print barcode labels for the rail',
  'nav.hintLists': 'Colours, categories, brands, suppliers and size runs',
  'nav.hintReceive': 'Put a delivery from a supplier into stock',
  'nav.hintAdjust': 'Write off something damaged, lost or given away',
  'nav.hintStockCount': 'Count the shelves and fix the numbers',
  'nav.hintSales': 'Every receipt, searchable',
  'nav.hintCustomers': 'Regulars, their phone numbers and what they bought',
  'nav.hintOwed': 'Who took something on account and has not paid yet',
  'nav.hintBills': 'Deliveries the shop has not paid for yet',
  'nav.hintCredits': 'Credit notes you gave out, and what is left on them',
  'nav.hintExpenses': 'Rent, electricity and anything else you paid out',
  'nav.hintReports': 'Sales, profit, VAT and what is selling',
  'nav.hintSettings': 'Shop name, VAT, currencies, receipt wording',
  'nav.hintUsers': 'Who can sign in',
  'nav.hintRoles': 'What each kind of staff member is allowed to do',
  'nav.hintDevices': 'The tills and tablets that sell on this shop',
  'nav.hintAudit': 'Who did what, and when',

  // --- connection -----------------------------------------------------------
  'net.online': 'Online',
  'net.offline': 'Offline',
  'net.syncing': 'Sending',
  'net.waiting': '{n} waiting',
  'net.offlineNote': 'You can keep selling. Everything is saved on this device and will upload by itself.',
  'net.syncedNow': 'Everything is up to date',
  'net.staleWarning': 'Sales have been waiting to upload for more than {h} hours. Check the internet.',
  'net.needOnline': 'This needs an internet connection.',
  'net.stockMayBeOld': 'Stock numbers may be out of date while this device is offline.',
  'net.syncNow': 'Send now',
  'net.refreshCatalog': 'Refresh the product list',
  'net.storageTitle': 'Keeping data on this device',
  'net.storageDenied': "The browser has not promised to keep this device's data. If the disk fills up, sales waiting to upload could be lost. Install the app to the home screen or desktop, then ask again.",
  'net.storageGranted': "The browser has promised to keep this device's data.",
  'net.storageUnsupported': 'This browser cannot promise to keep data. Use Chrome or Edge on this device.',
  'net.storageRetry': 'Ask again',
  'net.storageNowKept': "The browser will now keep this device's data.",
  'net.storageStillNo': 'The browser still says no. Installing the app to the home screen or desktop usually changes that.',
  'net.storageIos': "iPhone and iPad do not offer this promise - Safari always says no, and no setting changes it. Installed to the Home Screen your data is kept, but iOS can clear it if the app is not opened for a few weeks. So this is fine for price checks and reports; do not leave sales waiting to upload on this device.",
  'net.storageIosShort': 'iPhone cannot promise to keep data. Do not leave unsent sales on this device.',
  'net.storageDeniedShort': "This device may lose sales that are waiting to upload. Settings → Tills explains.",
  'net.storageUnsupportedShort': 'This browser cannot promise to keep data. Use Chrome or Edge.',

  // --- checkout -------------------------------------------------------------
  'checkout.title': 'Checkout',
  'checkout.scanPrompt': 'Scan an item to start',
  'checkout.scanHint': 'Or search by name above',
  'checkout.empty': 'Nothing on this sale yet',
  'checkout.addItem': 'Add item',
  'checkout.complete': 'Complete sale',
  'checkout.completed': 'Sale completed',
  'checkout.newSale': 'New sale',
  'checkout.hold': 'Hold this sale',
  'checkout.held': 'Sale held',
  'checkout.cancelSale': 'Cancel this sale',
  'checkout.cancelConfirm': 'Everything on this sale will be removed. The sale is not recorded.',
  'checkout.removeLine': 'Remove',
  'checkout.lineDiscount': 'Discount this line',
  'checkout.saleDiscount': 'Discount the whole sale',
  'checkout.changeSize': 'Change size',
  'checkout.unknownBarcode': 'No item has the barcode {code}.',
  'checkout.searchInstead': 'Search for it',
  'checkout.createProduct': 'Add it as a new product',
  'checkout.outOfStock': 'Only {n} left in stock',
  'checkout.noStock': 'This size is out of stock',
  'checkout.priceEach': 'What is the price?',
  'checkout.priceForEach': '{n} of them, {total} altogether',
  'catalog.openPrice': 'Price typed at the till',
  'catalog.openPriceTile': 'Price at the till',
  'catalog.openPriceHint': 'For things with no price of their own. The cashier types the price when they ring it up, and no stock is counted for it.',
  'checkout.sellAnyway': 'Sell anyway',
  'checkout.finalSale': 'Final sale - cannot be exchanged',
  'checkout.pay': 'Take payment',
  'checkout.itemsOne': '1 item',
  'checkout.itemsTwo': '2 items',
  'checkout.itemsCount': '{n} items',
  'checkout.itemsMany': '{n} items',
  'checkout.shortcuts': 'Keyboard: F2 pay, F3 search, F4 hold, F8 new sale',

  // --- payment --------------------------------------------------------------
  'pay.title': 'Take payment',
  'pay.tab': 'On account',
  'pay.payTabWho': 'On account · name the customer',
  'pay.payTabNamed': 'On account · {amount} · {name}',
  'pay.tabNote': '{name} takes this now and pays later. It goes on what they owe.',
  'pay.tabNeedsCustomer': 'Put the customer\'s name on the sale first — a debt with no name is not a debt.',
  'tab.title': 'Money owed to the shop',
  'tab.owes': 'Owes',
  'tab.nobody': 'Nobody owes the shop anything',
  'tab.nobodyHint': 'Sales taken on account will show up here.',
  'tab.total': 'Owed in total',
  'tab.people': '{n} customers',
  'tab.peopleOne': '1 customer',
  'tab.peopleTwo': '2 customers',
  'tab.peopleMany': '{n} customers',
  'tab.takePayment': 'Take a payment',
  'tab.takePaymentFrom': 'Payment from {name}',
  'tab.owesNow': 'Owes right now',
  'tab.howMuch': 'How much are they paying?',
  'tab.payAll': 'All of it',
  'tab.paid': 'Payment recorded',
  'tab.tooMuch': 'That is more than they owe.',
  'tab.since': 'Oldest',
  'tab.lastPaid': 'Last paid',
  'tab.never': 'never',
  'tab.statement': 'What this is made of',
  'tab.onAccount': 'Taken on account',
  'tab.paidOff': 'Paid',
  'tab.noCustomer': 'This sale needs a customer',
  'pay.payCash': 'Cash · {amount}',
  'pay.payCard': 'Card · {amount}',
  'pay.gaveMe': 'Or tap what they handed you',
  'pay.otherWay': 'Another way to pay',
  'pay.method': 'How is the customer paying?',
  'pay.cash': 'Cash',
  'pay.card': 'Card',
  'pay.credit': 'Store credit',
  'pay.other': 'Other',
  'pay.cardNote': 'Type the amount into the bank terminal, then confirm here.',
  'pay.amountDue': 'Amount due',
  'pay.amountIn': 'Amount in {currency}',
  'pay.tendered': 'Amount handed over',
  'pay.exact': 'Exact',
  'pay.changeDue': 'Change to give',
  'pay.stillDue': 'Still to pay',
  'pay.addPayment': 'Add another payment',
  'pay.split': 'Split the payment',
  'pay.rounding': 'Cash rounding',
  'pay.roundingNote': 'Cash totals round to the nearest 10 agorot.',
  'pay.confirm': 'Confirm payment',
  'pay.creditCode': 'Scan or type the credit code',
  'pay.creditBalance': 'Balance on this credit: {amount}',
  'pay.creditNotEnough': 'That credit does not have enough on it.',
  'pay.creditNeedsOnline': 'Store credit has to be checked online, so it cannot be used while offline.',
  'pay.gift': 'Gift card',
  'pay.giftCode': 'Scan the card, or type its number',
  'pay.giftBalance': 'Balance on this card: {amount}',
  'pay.giftNotFound': 'No gift card with that number.',
  'pay.giftOnlyHas': 'This gift card has {amount} on it. Take that, then collect the rest.',
  'pay.creditOnlyHas': 'This credit has {amount} on it. Take that, then collect the rest.',
  'pay.creditNotFound': 'No store credit with that code.',
  'pay.giftNotEnough': 'That gift card does not have enough on it.',
  'pay.giftNeedsOnline': 'A gift card has to be checked online, so it cannot be used while offline.',
  'pay.noChangeFrom': 'There is no change from {method}. Take only what is owed.',
  'nav.giftCards': 'Gift cards',
  'nav.hintGiftCards': 'Sell a card, or see what is left on one',
  'gift.title': 'Gift cards',
  'gift.sell': 'Sell a gift card',
  'gift.amount': 'What is the card worth?',
  'gift.cardNumber': 'Card number',
  'gift.cardNumberHint': 'Scan the card you are selling. Leave this empty and the till prints a voucher instead.',
  'gift.printedVoucher': 'Print a voucher',
  'gift.expiry': 'Last day it can be used',
  'gift.expiryHint': 'Leave empty and it never runs out.',
  'gift.takePayment': 'Take the money',
  'gift.sold': 'Gift card {code} sold for {amount}',
  'gift.printCard': 'Print the card',
  'gift.empty': 'No gift cards yet. Sell one and it will show here.',
  'gift.outstanding': 'Cards still unspent',
  'gift.outstandingHint': 'Money taken for goods that have not left the shop yet.',
  'gift.liveCards': '{n} cards',
  'gift.remaining': 'Left on it',
  'gift.spent': 'Spent',
  'gift.soldOn': 'Sold',
  'gift.expired': 'Expired',
  'gift.usedUp': 'Used up',
  'gift.searchPlaceholder': 'Card number, name or phone',
  'gift.notASale': 'Selling a card is not a sale: the money is in the drawer, and the VAT is charged when the card is spent.',
  'gift.voucherTitle': 'GIFT CARD',
  'gift.voucherNote': 'Present this at the till. It can be spent over more than one visit.',
  'pay.payments': 'Payments',

  // --- receipt --------------------------------------------------------------
  'receipt.title': 'Receipt',
  'receipt.print': 'Print receipt',
  'receipt.gift': 'Print gift receipt',
  'receipt.reprint': 'Print again',
  'receipt.no': 'Receipt no.',
  'receipt.cashier': 'Served by',
  'receipt.thanks': 'Thank you',
  'receipt.exchangePolicy': 'Exchange policy',
  'receipt.vatNo': 'VAT no.',
  'receipt.giftNote': 'Gift receipt - no prices shown',
  'receipt.change': 'Change given',
  'receipt.paidWith': 'Paid with',
  'receipt.emailOrPrint': 'Give the customer their receipt',
  'receipt.skip': 'No receipt',

  // --- shift ----------------------------------------------------------------
  'shift.title': 'My shift',
  'shift.open': 'Open my shift',
  'shift.close': 'Close my shift',
  'shift.opened': 'Shift opened',
  'shift.closed': 'Shift closed',
  'shift.openingCash': 'Cash in the drawer now',
  'shift.closingCash': 'Count the cash now',
  'shift.countEach': 'Count each currency and type what you actually have.',
  'shift.expected': 'Expected',
  'shift.counted': 'Counted',
  'shift.difference': 'Difference',
  'shift.over': 'over',
  'shift.short': 'short',
  'shift.exact': 'exact',
  'shift.zReport': 'End of day report',
  'shift.printZ': 'Print the end of day report',
  'shift.takings': 'Taken during the shift',
  'shift.noShift': 'No shift is open',
  'shift.startedAt': 'Started',
  'shift.salesCount': 'Sales',
  'shift.drawerOpen': 'Open the drawer',
  'shift.drawerReason': 'Why does the drawer need opening?',

  // --- exchange -------------------------------------------------------------
  'exchange.title': 'Exchange an item',
  'exchange.scanReceipt': 'Scan the receipt',
  'exchange.scanHint': 'Scan the barcode on the receipt, or type the receipt number, the customer\'s name or their phone.',
  'exchange.findPlaceholder': 'Receipt number, name or phone',
  'exchange.searchBy': 'Search by receipt number, phone or date',
  'exchange.noReceipt': 'No receipt',
  'exchange.noReceiptWarn': 'Without a receipt a manager has to approve it, and the item is worth its lowest price in the last {days} days.',
  'exchange.chooseReturning': 'What is coming back?',
  'exchange.chooseNew': 'What are they taking instead?',
  'exchange.alreadyExchanged': 'Already exchanged',
  'exchange.returnedDamaged': 'Came back damaged',
  'exchange.damagedNote': 'It will not go back on the shelf.',
  'exchange.paidFor': 'Paid',
  'exchange.customerPays': 'The customer pays',
  'exchange.customerGets': 'The customer is owed',
  'exchange.evenSwap': 'Straight swap, nothing to pay',
  'exchange.asCredit': 'Give store credit',
  'exchange.asCash': 'Give cash back',
  'exchange.complete': 'Complete the exchange',
  'exchange.completed': 'Exchange completed',
  'exchange.printReceipt': 'Print the exchange receipt',
  'exchange.outsideWindow': 'This was bought {days} days ago, past the {window} day limit. A manager has to approve it.',
  'exchange.finalSaleBlocked': 'This was a final sale item. A manager has to approve the exchange.',
  'exchange.cashRefundOff': 'Cash refunds are switched off. The customer gets store credit.',
  'exchange.nothingSelected': 'Choose what is coming back first.',
  'exchange.soldOn': 'Sold on',
  'exchange.daysAgo': '{n} days ago',
  'exchange.giftPrivacy': 'This is a gift receipt. The printed receipt will not show prices.',
  'exchange.returnOnly': 'Return without taking anything',

  // --- manager approval -----------------------------------------------------
  'approval.title': 'A manager needs to approve this',
  'approval.reasonOverDiscount': 'The discount is above the {limit}% a cashier can give',
  'approval.reasonVoid': 'Cancelling a completed sale',
  'approval.reasonOutsideWindow': 'The exchange window has passed',
  'approval.reasonNoReceipt': 'There is no receipt',
  'approval.reasonFinalSale': 'This was a final sale item',
  'approval.reasonCashRefund': 'Giving cash back',
  'approval.approve': 'Approve',
  'approval.approved': 'Approved by {name}',

  // --- price check ----------------------------------------------------------
  'priceCheck.title': 'Price check',
  'priceCheck.scan': 'Scan any item',
  'priceCheck.hint': 'The price and every other size will show here.',
  'priceCheck.otherSizes': 'Other sizes and colours',
  'priceCheck.inStock': 'in stock',
  'priceCheck.soldOut': 'sold out',
  'priceCheck.scanAnother': 'Scan another',

  // --- products -------------------------------------------------------------
  'products.title': 'Products',
  'products.new': 'Add a product',
  'products.itemName': 'What is it called?',
  'products.itemNameHint': 'Linen shirt',
  'products.whatSizes': 'What sizes does it come in?',
  'products.whatColours': 'What colours? (you can skip this)',
  'products.addVariants': 'Add sizes or colours',
  'products.addVariantsHint': 'Tick what this style should also come in. What it already has is locked, and nothing that exists is changed.',
  'products.sizesToHave': 'Sizes',
  'products.coloursToHave': 'Colours',
  'products.willBeAdded': 'These will be added',
  'products.addNSquares': 'Add {n}',
  'products.nothingNew': 'Nothing new to add',
  'products.variantsAdded': '{n} added',

  'lists.title': 'Lists',
  'lists.searchPlaceholder': 'Find in this list',
  'bills.title': 'Supplier invoices',
  'bills.stillToPay': 'Still to pay',
  'bills.paidSoFar': 'Paid',
  'bills.left': 'Left to pay',
  'bills.partlyPaid': 'Part paid',
  'bills.openInvoice': 'Open',
  'bills.noInvoiceNo': 'Delivery',
  'bills.supplierTitle': 'Supplier',
  'bills.billedTotal': 'Bought in total',
  'bills.payments': 'Payments',
  'bills.noPayments': 'Nothing paid yet',
  'bills.noPaymentsHint': 'Record a payment when money goes to the supplier.',
  'bills.whatCameIn': 'What came in this delivery',
  'bills.piecesLines': '{pieces} pieces in {lines} lines',
  'bills.recordPayment': 'Record a payment',
  'bills.payingTo': 'Paying {supplier}. This invoice still has {amount} on it.',
  'bills.amountPaid': 'How much was paid?',
  'bills.payTheRest': 'Pay the rest ({amount})',
  'bills.tooMuch': 'That is more than the {amount} still on this invoice.',
  'bills.howPaid': 'How was it paid?',
  'bills.method_cash': 'Cash',
  'bills.method_bank': 'Bank transfer',
  'bills.method_cheque': 'Cheque',
  'bills.method_card': 'Card',
  'bills.paidOn': 'Day the money left',
  'bills.paidOnHint': 'Not today? Put the day it was actually paid.',
  'bills.paymentRecorded': 'Payment recorded',
  'bills.paymentRemoved': 'Payment removed',
  'bills.removePayment': 'Remove this payment',
  'bills.removePaymentConfirm': 'Remove the payment of {amount}? The invoice goes back to owing it.',
  'bills.totalOwed': 'Owed to suppliers',
  'bills.invoiceOne': '1 invoice',
  'bills.invoiceTwo': '2 invoices',
  'bills.invoiceFew': '{n} invoices',
  'bills.invoiceMany': '{n} invoices',
  'bills.paid': 'Paid',
  'bills.unpaid': 'Not paid',
  'bills.noSupplier': 'No supplier',
  'bills.allSettled': 'You owe nothing',
  'bills.allSettledHint': 'Every delivery received has been paid for.',
  'bills.none': 'No deliveries yet',
  'bills.noneHint': 'Deliveries appear here once you receive one.',
  'lists.intro': 'The words the shop describes its products with. Anything added here appears in the product form straight away. Nothing can be deleted, because old receipts still refer to it - rename it instead.',
  'lists.colours': 'Colours',
  'lists.categories': 'Categories',
  'lists.brands': 'Brands',
  'lists.suppliers': 'Suppliers',
  'lists.sizeScales': 'Size runs',
  'lists.addNew': 'Add new',
  'lists.empty': 'Nothing on this list yet',
  'lists.emptyHint': 'Press Add new to make the first one.',
  'lists.saved': 'Saved',
  'lists.nameEn': 'Name in English',
  'lists.nameAr': 'Name in Arabic',
  'lists.shade': 'The shade',
  'lists.shadeHint': 'This is the dot shown beside the colour on the till.',
  'lists.theSizes': 'The sizes, smallest first',
  'lists.theSizesHint': 'Separate them with commas, for example: S, M, L, XL',
  'lists.scaleEditNote': 'Changing this list affects products made from now on. Products that already exist keep the sizes they were given.',
  'products.howMany': 'How many do you have?',
  'products.howManyHint': 'Leave it at zero and put the delivery in later.',
  'products.howManyEach': 'This many of every size and colour. You can fix each one afterwards.',
  'products.moreDetails': 'More details',
  'products.saveProduct': 'Add it',
  'products.createdWithStock': 'Added, {n} in stock',
  'products.stockLater': 'Product added, but the stock did not save. Add it on the product page.',
  'products.edit': 'Edit product',
  'products.empty': 'No products yet',
  'products.emptyHint': 'Add your first product, then print its labels.',
  'products.firstLoad': 'Getting the product list for this device. This takes a moment the first time.',
  'products.sizeScale': 'Size scale',
  'products.chooseColours': 'Colours',
  'products.generateGrid': 'The system will create one item for every size and colour.',
  'products.gridPreview': '{n} items will be created',
  'products.costPrice': 'Cost price',
  'products.salePrice': 'Selling price (VAT included)',
  'products.season': 'Season',
  'products.finalSale': 'Final sale (cannot be exchanged)',
  'products.photo': 'Photo',
  'products.archived': 'Archived',
  'products.archive': 'Archive this product',
  'products.archiveNote': 'It stays in the reports and its barcodes are never reused.',
  'products.variants': 'Sizes and colours',
  'products.grid': 'Size and colour grid',
  'products.addSize': 'Add a size',
  'products.addColour': 'Add a colour',
  'products.priceOverride': 'Different price for this one',
  'products.lowStockAt': 'Warn me at',
  'products.extraBarcode': 'Add the maker barcode',
  'products.extraBarcodeHint': 'Scan the barcode already printed on the item. Both will find this size.',
  'products.created': 'Product added',
  'products.totalStock': 'Total stock',

  // --- stock ----------------------------------------------------------------
  'stock.receive': 'Receive stock',
  'stock.styleSummary': '{sizes} sizes · {colors} colours',
  'stock.gridLegend': 'Type how many arrived. The grey number is what is on the shelf.',
  'stock.newItem': 'New item',
  'stock.newItemHere': 'A new item in this delivery',
  'stock.addToDelivery': 'Add it to the delivery',
  'stock.newItemAdded': 'Added, {n} pieces on the delivery',
  'stock.newItemNotOnList': 'The item was created, but it could not be put on the list. Search for it by name.',
  'stock.emptyDelivery': 'Nothing on this delivery yet',
  'stock.emptyDeliveryHint': 'Scan a piece, search for it by name, or press New item if the shop has never carried it.',
  'stock.howManyCame': 'How many came in?',
  'stock.howManyCameEach': 'This many of every size and colour on the delivery.',
  'stock.howManyShort': 'How many',
  'stock.costHint': 'What you pay the supplier for one, not what you sell it for.',
  'stock.deliveryDetails': 'Supplier and invoice',
  'stock.piecesCount': '{n} pieces',
  'stock.piecesOne': '1 piece',
  'stock.piecesTwo': '2 pieces',
  'stock.piecesMany': '{n} pieces',
  'stock.costTotal': 'Cost of the delivery',
  'stock.putIntoStock': 'Put it into stock',
  'stock.receiveTitle': 'Receiving from a supplier',
  'stock.invoiceNo': 'Invoice number',
  'stock.vatIncluded': 'The invoice prices include VAT',
  'stock.unitCost': 'Cost each',
  'stock.received': 'Stock received',
  'stock.receivedThen': 'Print labels for what you just received?',
  'stock.printLabelsNow': 'Print labels',
  'stock.skipLabels': 'Not now',
  'stock.markPaid': 'Invoice is paid',
  'stock.adjust': 'Adjust stock',
  'stock.adjustTitle': 'Change a stock number',
  'stock.adjustReason': 'Why is it changing?',
  'stock.adjusted': 'Stock adjusted',
  'stock.damaged': 'Damaged',
  'stock.lost': 'Lost or stolen',
  'stock.countCorrection': 'Count correction',
  'stock.sample': 'Sample',
  'stock.other': 'Other',
  'stock.count': 'Stock count',
  'stock.countNew': 'Start a stock count',
  'stock.countName': 'What is this count called?',
  'stock.countScope': 'Count everything, or just one category',
  'stock.countScan': 'Scan each item on the shelf',
  'stock.countScanned': '{n} scanned',
  'stock.countReview': 'Check the differences',
  'stock.countApply': 'Apply the differences',
  'stock.countApplied': 'Stock count applied',
  'stock.countApplyWarn': 'This changes the stock numbers to what you counted. It cannot be undone.',
  'stock.expected': 'System says',
  'stock.counted': 'You counted',
  'stock.difference': 'Difference',
  'stock.noDifferences': 'Everything matches',
  'stock.movements': 'Stock history',
  'stock.lowStock': 'Low stock',
  'stock.outOfStock': 'Out of stock',
  'stock.inStock': 'In stock',

  // --- labels ---------------------------------------------------------------
  'labels.title': 'Labels',
  'labels.designer': 'Label designer',
  'labels.template': 'Label size',
  'labels.custom': 'Custom size',
  'labels.width': 'Width (mm)',
  'labels.height': 'Height (mm)',
  'labels.whatToShow': 'What goes on the label',
  'labels.showStoreName': 'Shop name',
  'labels.showNameEn': 'Name in English',
  'labels.showNameAr': 'Name in Arabic',
  'labels.showSize': 'Size',
  'labels.showColour': 'Colour',
  'labels.showPrice': 'Price',
  'labels.showBarcodeText': 'Barcode number underneath',
  'labels.howMany': 'How many of each',
  'labels.queue': 'Labels to print',
  'labels.addToQueue': 'Add to the print list',
  'labels.printAll': 'Print {n} labels',
  'labels.preview': 'Preview',
  'labels.empty': 'Nothing in the print list yet',
  'labels.emptyHint': 'Pick items and how many labels each needs.',
  'labels.printHint': 'Set the printer paper size to match the label, and turn margins off.',
  'labels.saveTemplate': 'Save this label size',

  // --- sales history --------------------------------------------------------
  'sales.title': 'Sales history',
  'sales.searchPlaceholder': 'Receipt number, customer name or phone',
  'sales.onDay': '{n} sales',
  'sales.void': 'Cancel this sale',
  'sales.voidReason': 'Why is it being cancelled?',
  'sales.voided': 'Cancelled',
  'sales.voidedOk': 'Sale cancelled',
  'sales.voidWarn': 'The sale stays in the records marked as cancelled, and the stock goes back.',
  'sales.viewReceipt': 'See the receipt',
  'sales.empty': 'No sales in this period',
  'sales.kindSale': 'Sale',
  'sales.kindExchange': 'Exchange',
  'sales.kindReturn': 'Return',

  // --- customers and credit -------------------------------------------------
  'customers.title': 'Customers',
  'customers.new': 'Add a customer',
  'customers.empty': 'No customers yet',
  'customers.emptyHint': 'Customers are only needed for store credit and for finding old purchases.',
  'customers.purchases': 'Past purchases',
  'customers.creditBalance': 'Store credit',
  'customers.tab': 'Tab balance',
  'customers.payTab': 'Record a payment on the tab',
  'customers.tabDisabled': 'Customer tabs are switched off in settings.',
  'credits.title': 'Store credit',
  'credits.new': 'Issue store credit',
  'credits.code': 'Credit code',
  'credits.issued': 'Issued',
  'credits.used': 'Used',
  'credits.remaining': 'Left',
  'credits.expires': 'Expires',
  'credits.expired': 'Expired',
  'credits.printVoucher': 'Print the voucher',
  'credits.outstanding': 'Outstanding store credit',
  'credits.empty': 'No store credit has been issued',

  // --- reports --------------------------------------------------------------
  'reports.title': 'Reports',
  'reports.sales': 'Sales',
  'reports.profit': 'Profit',
  'reports.vat': 'VAT',
  'reports.cash': 'Cash and shifts',
  'reports.exchanges': 'Exchanges and returns',
  'reports.inventory': 'Stock value',
  'reports.stock': 'Stock reports',
  'reports.purchases': 'Purchases',
  'reports.expenses': 'Expenses',
  'reports.pl': 'Profit and loss',
  'reports.grossSales': 'Gross sales',
  'reports.discounts': 'Discounts',
  'reports.returns': 'Returns and exchanges',
  'reports.netSales': 'Net sales',
  'reports.vatCollected': 'VAT collected',
  'reports.netExVat': 'Sales without VAT',
  'reports.transactions': 'Sales',
  'reports.averageSale': 'Average sale',
  'reports.itemsPerSale': 'Items per sale',
  'reports.itemsSold': 'Items sold',
  'reports.cogs': 'Cost of goods sold',
  'reports.grossProfit': 'Gross profit',
  'reports.margin': 'Margin',
  'reports.rounding': 'Cash rounding',
  'reports.byDay': 'By day',
  'reports.byHour': 'By hour',
  'reports.byCategory': 'By category',
  'reports.byBrand': 'By brand',
  'reports.byProduct': 'By product',
  'reports.bySize': 'By size',
  'reports.byColour': 'By colour',
  'reports.byCashier': 'By cashier',
  'reports.byPayment': 'By payment type',
  'reports.byCurrency': 'By currency',
  'reports.outputVat': 'VAT on sales',
  'reports.inputVat': 'VAT on purchases and expenses',
  'reports.purchaseVat': 'VAT on purchases',
  'reports.expenseVat': 'VAT on expenses',
  'reports.netPurchasesExVat': 'Purchases without VAT',
  'reports.netVat': 'VAT to pay',
  'reports.vatNote': 'Give this to your accountant for the VAT return.',
  'reports.expected': 'Expected in the drawer',
  'reports.counted': 'Counted',
  'reports.overShort': 'Over or short',
  'reports.atCost': 'At cost',
  'reports.atRetail': 'At selling price',
  'reports.potentialProfit': 'Profit if it all sells',
  'reports.bestSellers': 'Best sellers',
  'reports.deadStock': 'Not selling',
  'reports.deadStockNote': 'Nothing sold in {days} days, still in stock.',
  'reports.lastSold': 'Last sold',
  'reports.never': 'Never sold',
  'reports.mostExchanged': 'Most exchanged',
  'reports.exchangesBySize': 'Exchanges by size',
  'reports.sizeHint': 'A size that comes back often usually means it runs small or large.',
  'reports.creditIssued': 'Credit issued',
  'reports.creditUsed': 'Credit used',
  'reports.owedToSuppliers': 'Owed to suppliers',
  'reports.owedAllTime': 'Everything still unpaid, not only these dates',
  'reports.owedOfThese': 'Unpaid, of these',
  'reports.netProfit': 'Net profit',
  'reports.expensesExVat': 'Expenses without VAT',
  'reports.profitNote': 'Profit is worked out on sales and costs without VAT. The VAT is collected for the tax office and is not the shop\'s money.',
  'reports.noData': 'Nothing to show for these dates',
  'reports.vsLastWeek': 'vs the same day last week',

  // --- dashboard ------------------------------------------------------------
  'dash.title': 'Today',
  'dash.todaySales': 'Sales today',
  'dash.topToday': 'Selling today',
  'dash.cashInDrawer': 'Cash in the drawer',
  'dash.lowStockAlert': '{n} items are low on stock',
  'dash.openShifts': 'Open shifts',
  'dash.noSalesYet': 'No sales yet today',
  'dash.stockValue': 'Stock at cost',
  'dash.hourly': 'Through the day',

  // --- expenses -------------------------------------------------------------
  'expenses.title': 'Expenses',
  'expenses.new': 'Record an expense',
  'expenses.category': 'What was it for?',
  'expenses.rent': 'Rent',
  'expenses.salaries': 'Salaries',
  'expenses.electricity': 'Electricity',
  'expenses.water': 'Water',
  'expenses.cleaning': 'Cleaning',
  'expenses.packaging': 'Bags and packaging',
  'expenses.transport': 'Transport',
  'expenses.other': 'Other',
  'expenses.spentOn': 'Date',
  'expenses.vatOnIt': 'VAT included in this',
  'expenses.empty': 'No expenses recorded',

  // --- settings -------------------------------------------------------------
  'settings.title': 'Settings',
  'settings.store': 'The shop',
  'settings.tillTab': 'At the till',
  'settings.idleSignOut': 'Sign out when nobody uses the till',
  'settings.idleSignOutNote': 'Never suits a till behind the counter. A till is never signed out while the internet is down.',
  'settings.idleNever': 'Never',
  'settings.idleAfter': 'After {n} minutes',
  'settings.tillNote': 'Switch off the parts of the till this shop does not use. Nothing is deleted, and everything comes back the moment you switch it on again.',
  'settings.useShifts': 'Count the drawer open and closed',
  'settings.useShiftsNote': 'Cashiers open a shift with a cash count and close it with an end-of-day report. Leave this off if one person runs the till.',
  'settings.useHold': 'Let a sale be parked and picked up later',
  'settings.useHoldNote': 'For when somebody goes to try something on and the next customer is waiting.',
  'settings.storeName': 'Shop name',
  'settings.address': 'Address',
  'settings.taxNumber': 'VAT registration number',
  'settings.logo': 'Logo',
  'settings.currencies': 'Currencies and rates',
  'settings.rateToday': "Today's rate",
  'settings.ratePerUnit': 'Shekels for one {code}',
  'settings.rateHistory': 'Past rates',
  'settings.rateSaved': 'Rate saved',
  'settings.rateNote': 'Every sale keeps the rate that was used, so old receipts never change.',
  'settings.vat': 'VAT',
  'settings.vatRate': 'VAT rate (%)',
  'settings.vatNote': 'Shelf prices include VAT.',
  'settings.cashStep': 'Round cash to (agorot)',
  'settings.receipt': 'Receipts',
  'settings.receiptLang': 'Receipt language',
  'settings.receiptBoth': 'Both',
  'settings.receiptFooter': 'Message at the bottom',
  'settings.exchanges': 'Exchange rules',
  'settings.exchangeWindow': 'Exchange allowed for (days)',
  'settings.allowCashRefund': 'Allow cash refunds',
  'settings.allowCashRefundNote': 'Off by default. When on, someone senior still has to authorise it every time.',
  'settings.noReceiptLookback': 'Value a no-receipt return on the lowest price in (days)',
  'settings.enforceFinalSale': 'Block exchanges on final sale items',
  'settings.maxDiscount': 'Most a cashier can discount (%)',
  'settings.lowStockDefault': 'Warn on low stock at',
  'settings.appearance': 'Look',
  'settings.theme': 'Light or dark',
  'settings.themeHint': 'Changes here show straight away, on every till once you save.',
  'settings.themeAuto': 'Follow the device',
  'settings.themeAutoHint': 'Each till follows its own phone or computer setting. Right now that is',
  'settings.themeLight': 'Light',
  'settings.themeDark': 'Dark',
  'settings.accent': 'The shop colour',
  'settings.accentHint': 'Pick one colour. Buttons, highlights and headings follow it, and the text on them is chosen so it can always be read.',
  'settings.accentCustom': 'Or your own colour',
  'settings.accentReset': 'Back to the original',
  'settings.accentPreview': 'Preview',
  'settings.deadStockDays': 'Call stock dead after (days)',
  'settings.customerTabs': 'Allow customer tabs',
  'settings.arabicDigits': 'Use Arabic-Indic digits (٠١٢٣)',
  'settings.unsyncedWarn': 'Warn if sales wait more than (hours)',
  'settings.staff': 'Staff',
  'settings.addUser': 'Add someone',
  'settings.role': 'What can they do?',
  'settings.roleOwner': 'Owner - everything',
  'settings.roleManager': 'Manager - stock, reports, approvals',
  'settings.roleCashier': 'Cashier - selling and exchanges',
  'settings.passwordNote': 'At least 8 characters. Everyone signs in with one.',
  'settings.maxDiscountUser': 'Most this person can discount (%)',
  'settings.devices': 'Tills and devices',
  'settings.deviceSeries': 'Receipt prefix',
  'settings.deviceSeriesNote': 'Each till has its own receipt prefix, so two tills selling with no internet can never produce the same receipt number.',
  'settings.deviceLastSeen': 'Last seen',
  'settings.deviceThis': 'This device',
  'settings.backups': 'Backups',
  'settings.backupNow': 'Make a backup now',
  'settings.backupNote': 'A backup is made automatically every night at 2am. Copy it somewhere else too.',
  'settings.backupMade': 'Backup made',
  'settings.rebuildStock': 'Rebuild stock from the ledger',
  'settings.rebuildNote': 'Recounts every stock number from its history. Safe to run at any time.',
  'settings.rebuilt': 'Stock rebuilt',
  'settings.audit': 'Activity log',
  'settings.paymentMethods': 'Payment types',

  // --- roles and permissions ------------------------------------------------
  'nav.roles': 'Roles',
  'roles.title': 'Roles and permissions',
  'roles.intro': 'A role is a name with a list of what that person may do. Add as many as the shop needs.',
  'roles.new': 'Add a role',
  'roles.builtIn': 'Comes with the system',
  'roles.custom': 'Yours',
  'roles.editPermissions': 'Change what they can do',
  'roles.permissionCount': '{n} things they can do',
  'roles.people': '{n} people',
  'roles.delete': 'Delete this role',
  'roles.deleteConfirm': 'Nobody has this role, so nothing will change for any of your staff.',
  'roles.deleteBlocked': '{n} people still have this role. Move them to another role first.',
  'roles.ownerLocked': 'The Owner role always has everything. That is deliberate: it is what stops the shop locking itself out of its own system.',
  'roles.sensitive': 'sensitive',
  'roles.askOwner': 'Ask the owner to add this to your role.',
  'roles.forOnePerson': 'Just for this person',
  'roles.overrideHint': 'Tap anything to give this one person a power their role does not include, or take one away. Nobody else with the same role is affected.',
  'roles.fromRole': 'from the role',
  'roles.added': 'added',
  'roles.removed': 'taken away',
  'roles.role': 'Role',
  'roles.permissionsTotal': 'Can do {n} things in total',
  'roles.noOverrides': 'Exactly what the role says',
  'roles.overrideSummary': '{added} added, {removed} taken away',
  'roles.lastOwner': 'This is the only owner. Make someone else an owner first.',
  'roles.discountLimit': 'Can discount up to {n}%',
  'roles.discountLimitLabel': 'Most this role can discount (%)',
  'settings.readOnly': 'You can see these settings but not change them.',
  'checkout.resume': 'Put it back on the till',
  'checkout.someItemsGone': 'Some items are no longer in the shop and were left out.',
  'exchange.scanTheItem': 'Scan the item being brought back',
  'exchange.noReceiptOffline': 'Offline, so the item is valued at today\u2019s price.',
  'exchange.noReceiptNoHistory': 'This has not sold recently, so today\u2019s price was used.',
  'checkout.addCustomer': 'Name the customer (optional)',
  'checkout.noCustomer': 'No customer',
  'approval.wrongLogin': 'That username or password is not right, or that person cannot approve this.',
  'settings.newPassword': 'New password',
  'settings.passwordKeep': 'Leave this empty to keep the current password.',
  'settings.inactive': 'Not in use',
  'settings.deviceNextReceipt': 'Receipts from this till will look like {n}',
  'checkout.searchOrScan': 'Search or scan',
  'checkout.emptyTitle': 'Nothing on this sale yet',
  'checkout.emptyHint': 'Tap an item, or scan its barcode.',
  'checkout.takePayment': 'Take payment',
  'checkout.showSale': 'Show the sale',
  'checkout.hideSale': 'Hide',
  'roles.modules': 'What this system is made of',
  'roles.moduleNote': 'Each part can be worked on, replaced or switched off on its own.',

  // --- errors ---------------------------------------------------------------
  'error.title': 'Something went wrong',
  'error.generic': 'That did not work. Please try again.',
  'error.network': 'Cannot reach the system. Check the internet.',
  'error.notAllowed': 'You do not have access to this.',
  'error.switchedOff': 'This part is switched off',
  'error.switchedOffHint': 'The owner can turn it on again in Settings, under "At the till".',
  'error.notFound': 'That was not found.',
  'error.sessionEnded': 'Your session ended. Please sign in again.',
  'error.tryAgain': 'Try again',
  'error.goBack': 'Go back',
  'setup.title': 'Set up your shop',
  'setup.intro': 'A few details, and the till, the sign-in screen and every receipt carry your shop.',
  'setup.later': 'Set up later',
  'setup.step_shop': 'Your shop',
  'setup.step_details': 'Contact & tax',
  'setup.step_receipt': 'Receipts',
  'setup.step_look': 'Colours',
  'setup.step_password': 'Password',
  'setup.step_done': 'Done',
  'setup.shopH': 'What is your shop called?',
  'setup.shopP': 'Shown on the till, the sign-in screen and every receipt. Fill in both languages if you can.',
  'setup.logo': 'Logo',
  'setup.logoHint': 'PNG or JPG. It is shrunk for you; a logo on a plain background prints best.',
  'setup.logoUpload': 'Upload logo',
  'setup.logoChange': 'Change logo',
  'setup.logoRemove': 'Remove',
  'setup.logoNotImage': 'That file is not a picture.',
  'setup.logoUnreadable': 'That picture could not be opened.',
  'setup.logoTooBig': 'That picture is too large, even shrunk. Try a simpler file.',
  'setup.previewMenu': 'How it looks in the menu',
  'setup.previewOwner': 'Owner',
  'setup.detailsH': 'Printed at the top of every receipt',
  'setup.detailsP': 'Leave out anything you do not want printed: an empty line simply does not appear.',
  'setup.vatHint': '16 in Palestine. 0 if you do not charge VAT.',
  'setup.receiptH': 'Your receipts',
  'setup.receiptP': 'Which language they print in, and the line at the bottom.',
  'setup.previewReceipt': 'Preview',
  'setup.lookH': 'Your colours',
  'setup.lookP': 'Pick your shop colour, and light or dark screens. You can change it any time in Settings.',
  'setup.pwH': 'Choose your own password',
  'setup.pwP': 'You were given a password to get started. Replace it with one only you know.',
  'setup.pwMin': 'At least 8 characters.',
  'setup.pwChange': 'Change my password',
  'setup.pwKeep': 'Keep my current password',
  'setup.pwDone': 'Password changed.',
  'setup.doneH': '{name} is ready',
  'setup.doneP': 'Everything here can be changed later in Settings. What would you like to do first?',
  'setup.doneProducts': 'Add my products',
  'setup.doneStaff': 'Add my staff',
  'setup.doneSell': 'Start selling',
  'login.tillLimit': 'All of this shop\'s tills are in use. The owner can free one under Tills, or ask AppBay for more.',
  'tills.alertStuck': '{till}: {n} sale(s) could not upload and need checking.',
  'tills.alertWaiting': '{till} has had {n} sale(s) waiting to upload for {h} hour(s).',
  'tills.see': 'See tills',
  'tills.inUse': '{n} of {max} tills in use',
  'tills.limitNote': 'Each computer that sells gets its own till and receipt series. Retire one you no longer use to free its place.',
  'tills.onTill': 'On the till',
  'tills.stuck': '{n} need checking',
  'tills.waiting': '{n} waiting',
  'tills.allUp': 'All uploaded',
  'tills.retire': 'Retire',
  'tills.restore': 'Bring back',
  'tills.retiredPill': 'Retired',
  'tills.retired': 'Till retired. Its place is free.',
  'tills.restored': 'Till brought back.',
  'tills.retireTitle': 'Retire {till}?',
  'tills.retireMsg': 'Its sales and receipt numbers are kept, and it can be brought back. The computer using it will need a free till to sell again.',
  'tills.retireWarn': 'This till still holds {n} sale(s) that have not reached the server.',
  'pf.stuck': '{n} stuck',
  'pf.waiting': '{n} waiting',
  'pf.stuckLong': '{n} sale(s) on this shop\'s tills were refused by the server and need checking.',
  'pf.waitingLong': '{n} sale(s) are waiting to upload from this shop\'s tills, the oldest since {when}.',
  'pf.secTills': 'Tills',
  'pf.tillsHint': 'How many computers this shop may sell from at once. Each gets its own receipt series.',
  'pf.tillsAllowed': 'Tills allowed',
  'pf.tillsOf': '{n} of {max}',
  'pf.ev.tills_changed': 'Changed tills allowed',
  'sub.endsOn': 'Your AppBay subscription ends on {date}.',
  'sub.ended': 'Your AppBay subscription ended on {date}.',
  'sub.unpaid': 'Your AppBay subscription has not been paid yet.',
  'sub.contact': 'Contact AppBay to renew',
  'sub.waText': 'Hello AppBay, I would like to renew our subscription.',
  'pf.secBilling': 'Subscription',
  'pf.bill_not_billed': 'Not billed',
  'pf.bill_unpaid': 'Not paid',
  'pf.bill_active': 'Paid',
  'pf.bill_due_soon': 'Due soon',
  'pf.bill_overdue': 'Overdue',
  'pf.paidUntilLong': 'Paid until {date} ({n} days left).',
  'pf.endedOn': 'Ended on {date}, {n} days ago.',
  'pf.notBilledLong': 'No price set: this shop is not billed.',
  'pf.neverPaid': 'No payment recorded yet.',
  'pf.price': 'Price',
  'pf.notBilled': 'not billed',
  'pf.period': 'Every',
  'pf.yearly': 'Year',
  'pf.monthly': 'Month',
  'pf.autoPause': 'When it is overdue',
  'pf.autoPauseHint': 'Off: the shop keeps working and its owner is reminded. On: it is paused after the grace days.',
  'pf.autoPauseOn': 'Pause the shop after',
  'pf.graceDays': 'days',
  'pf.recordPayment': 'Record a payment',
  'pf.amount': 'Amount',
  'pf.paidOn': 'Paid on',
  'pf.coversUntil': 'Covers until',
  'pf.note': 'Note',
  'pf.notePlaceholder': 'cash, bank transfer, trial...',
  'pf.record': 'Record',
  'pf.paymentRecorded': 'Payment recorded.',
  'pf.paymentRemoved': 'Payment removed.',
  'pf.noPayments': 'No payments yet. A free trial is a payment of 0 that covers the trial.',
  'pf.remove': 'Remove',
  'pf.removePaymentTitle': 'Remove this payment?',
  'pf.removePaymentMsg': '{amount} paid on {date}. "Paid until" is worked out again from the payments that are left.',
  'pf.kpiYearly': 'Subscriptions, per year',
  'pf.kpiYearlyHint': 'active shops, monthly prices counted ×12',
  'pf.kpiLate': 'Overdue or not paid',
  'pf.dueSoonCount': '{n} due in the next 14 days',
  'pf.activePaused': '{a} active · {p} paused',
  'pf.colPaidUntil': 'Paid until',
  'pf.ev.billing_changed': 'Changed the price',
  'pf.ev.payment_recorded': 'Recorded a payment',
  'pf.ev.payment_removed': 'Removed a payment',
  'pf.ev.auto_paused': 'Paused automatically: overdue',
  'forgot.link': 'Forgot your password?',
  'forgot.title': 'Forgot your password',
  'forgot.intro': 'Your username goes to the person who can give you a new password: the shop owner, or AppBay for the owner.',
  'forgot.message': 'A note (optional)',
  'forgot.messageHint': 'For example the number to call you back on.',
  'forgot.send': 'Send the request',
  'forgot.back': 'Back to sign in',
  'forgot.sent': 'Request sent. When a new password is set for you, you will be able to sign in with it.',
  'forgot.ownerHint': 'Are you the shop owner?',
  'forgot.whatsapp': 'Message AppBay on WhatsApp',
  'forgot.waText': 'Hello AppBay, I forgot my password. Account code: {code}',
  'forgot.alert': '{n} person(s) asked for a new password.',
  'forgot.alertLink': 'See staff',
  'forgot.requestsTitle': 'Asked for a new password',
  'forgot.setNew': 'Set a new password',
  'forgot.dismiss': 'Dismiss',
  'pf.pwRequests': '{n} password request(s)',
  'pf.pwRequest': '{name} asked for a new password {when}.',
  'pf.pwRequestOwner': 'The owner, {name}, asked for a new password {when}. Only you can set it.',
  'pf.ev.password_request_dismissed': 'Dismissed a password request',
  'pf.navErrors': 'Errors',
  'pf.errorsSub': 'Errors from every till, this panel and the server. The same error from many tills is one row.',
  'pf.errorsShowFixed': 'Show fixed ones',
  'pf.errorsNone': 'No errors',
  'pf.errorsNoneHint': 'When a till or the server hits a problem, it appears here by itself.',
  'pf.errServer': 'Server',
  'pf.errTill': 'Till',
  'pf.errPanel': 'Panel',
  'pf.errTimes': '{n} time(s)',
  'pf.errLast': 'last {when}',
  'pf.errFirst': 'First seen {when}',
  'pf.errFixed': 'Fixed',
  'pf.errMarkFixed': 'Mark fixed',
  'pf.ev.error_marked_fixed': 'Marked an error fixed',
  'import.fromExcel': 'Import from Excel',
  'import.title': 'Import products from Excel',
  'import.intro': 'Bring in all your products at once: one row for each size in each colour. Nothing is saved until you have seen the check.',
  'import.step1': 'Download the template and fill it in - or use your own sheet: columns are found by their headings.',
  'import.getTemplate': 'Download the template',
  'import.step2': 'Choose the file (.xlsx or .csv).',
  'import.choose': 'Choose file',
  'import.chooseAnother': 'Choose another file',
  'import.step3': 'Read the check, then press Import.',
  'import.newProducts': 'New products',
  'import.addedTo': '+ new sizes for {n} existing',
  'import.pieces': 'Pieces',
  'import.piecesHint': 'a size in a colour',
  'import.stock': 'In stock',
  'import.already': 'Already in the system',
  'import.alreadyHint': 'left as they are',
  'import.errorsTitle': '{n} problem(s) - nothing is imported until they are fixed',
  'import.errorsHint': 'Fix these rows in your file, save it, and choose it again.',
  'import.row': 'Row',
  'import.problem': 'Problem',
  'import.more': '...and {n} more',
  'import.readFrom': 'Read from sheet "{sheet}", headings on row {row}:',
  'import.ignored': 'Columns not used:',
  'import.willCreate': 'These will be added to your lists:',
  'import.create.categories': 'Categories',
  'import.create.colours': 'Colours',
  'import.create.brands': 'Brands',
  'import.create.suppliers': 'Suppliers',
  'import.sizesColours': 'Sizes and colours',
  'import.addsTo': 'Adds to existing',
  'import.inSystem': 'Already in the system',
  'import.rowN': 'Row {n}',
  'import.fixFirst': 'Fix the problems above first.',
  'import.nothingNew': 'Everything in this file is already in the system.',
  'import.readyHint': 'Everything checks out. Importing saves it all at once - or nothing, if anything goes wrong.',
  'import.go': 'Import {n} pieces',
  'import.doneTitle': 'Imported',
  'import.doneLine': '{products} new products, {addedTo} added to, {pieces} pieces, {qty} in stock. The tills get them within minutes.',
  'import.seeProducts': 'See the products',
  'import.file.old_xls': 'This is an old Excel file (.xls). Open it in Excel, save it as "Excel Workbook (.xlsx)", then choose it again.',
  'import.file.csv_not_utf8': 'This CSV file would turn Arabic into nonsense. In Excel choose Save As, then "CSV UTF-8" - or save it as .xlsx.',
  'import.file.unreadable': 'That file could not be read. Save it as .xlsx and try again.',
  'import.file.no_header': 'No column headings were found. The top rows need headings such as Name, Size, Colour, Price - or use the template.',
  'import.file.no_name_column': 'There is no column for the product name. Add one headed Name (or الاسم).',
  'import.file.too_many_rows': 'More than 5,000 rows. Split the file in two and import each.',
  'import.file.too_big': 'That file is over 5 MB. Remove pictures or extra sheets from it, or split it.',
  'import.file.empty': 'That file is empty.',
  'import.err.no_name': 'No product name.',
  'import.err.no_price': 'No price for this product - at least one of its rows needs one.',
  'import.err.bad_price': 'The price "{value}" is not a number.',
  'import.err.bad_cost': 'The cost "{value}" is not a number.',
  'import.err.bad_qty': 'The quantity "{value}" is not a whole number.',
  'import.err.bad_barcode': 'The barcode "{value}" cannot be used (English letters and digits only, up to 48).',
  'import.err.dup_piece': 'Same product, size and colour as row {value}.',
  'import.err.dup_barcode': 'Same barcode as row {value}.',
  'import.err.barcode_taken': 'This barcode is already on another product ({value}).',
  'import.err.archived_code': 'The code {value} belongs to an archived product. Use another code, or bring that product back first.',
  'import.err.ambiguous_name': 'More than one product is called "{value}". Add a Style code column so the right one is found.',
  'import.err.no_stock_permission': 'The file has quantities, and your role cannot receive stock. Empty the Quantity column, or ask the owner.',
  'import.warn.no_cost': '{n} piece(s) have no cost. Profit reports count them as costing nothing until you add it.',
  'import.warn.qty_on_existing': '{n} row(s) with a quantity are for pieces already in the system. Their stock is left alone - new stock comes in through Receiving.',
  'import.warn.suppliers_off': 'The Supplier column is not used: suppliers are not switched on for this shop.',
  'photo.add': 'Add a photo',
  'photo.change': 'Change',
  'photo.remove': 'Remove',
  'photo.saving': 'Saving the photo...',
  'photo.saved': 'Photo saved. The tills show it within minutes.',
  'photo.removeTitle': 'Remove the photo?',
  'photo.removeMsg': 'The product stays; only its picture goes.',
  'photo.err.not_image': 'That file is not a picture.',
  'photo.err.unreadable': 'That picture could not be opened. Try a JPG or PNG.',
  'photo.err.too_big': 'That picture is too large.',
  'photo.err.save': 'The photo could not be saved.',
  'nav.promotions': 'Promotions',
  'nav.hintPromotions': 'Percent off and buy-X-get-Y',
  'promo.onSale': 'Promotions',
  'promo.intro': 'Offers the till applies by itself, offline too, and prints on the receipt. Once one starts it can be stopped, not changed.',
  'promo.new': 'New promotion',
  'promo.editTitle': 'Change the promotion',
  'promo.tabCurrent': 'Running and coming up',
  'promo.tabOver': 'Ended',
  'promo.none': 'No promotions yet',
  'promo.noneHint': 'For example: 20% off all dresses this weekend, or buy 2 get 1 free on T-shirts.',
  'promo.noneOver': 'No ended promotions',
  'promo.status.running': 'Running',
  'promo.status.soon': 'Coming up',
  'promo.status.over': 'Ended',
  'promo.appliesTo': 'Applies to:',
  'promo.scopeAll': 'everything in the shop',
  'promo.nProducts': '{n} product(s)',
  'promo.untilStopped': 'until stopped',
  'promo.statSales': '{n} sales',
  'promo.statPieces': '{n} pieces',
  'promo.statGiven': '{amount} off',
  'promo.stop': 'Stop',
  'promo.remove': 'Remove',
  'promo.stopTitle': 'Stop this promotion now?',
  'promo.stopMsg': 'The tills stop applying it within minutes. Sales already made keep their promotion.',
  'promo.removeTitle': 'Remove this promotion?',
  'promo.removeMsg': 'It has not started, so nothing was sold under it.',
  'promo.removed': 'Promotion removed.',
  'promo.stoppedOk': 'Promotion stopped.',
  'promo.savedRunning': 'Saved. The tills apply it within minutes.',
  'promo.savedSoon': 'Saved. It starts by itself on the date you set.',
  'promo.nameAr': 'Name in Arabic',
  'promo.nameEn': 'Name in English',
  'promo.kind': 'What kind',
  'promo.kindPercent': 'Percent off',
  'promo.kindBxgy': 'Buy X, get Y',
  'promo.percentOff': 'Percent off',
  'promo.buy': 'Buy',
  'promo.get': 'Get',
  'promo.at': 'At',
  'promo.free': 'Free',
  'promo.half': 'Half price',
  'promo.scope.all': 'Everything',
  'promo.scope.categories': 'Categories',
  'promo.scope.brands': 'Brands',
  'promo.scope.products': 'Products',
  'promo.nothingToPick': 'Nothing here yet.',
  'promo.chosen': '{n} chosen',
  'promo.starts': 'Starts',
  'promo.ends': 'Ends',
  'promo.endsHint': 'Empty: until you stop it.',
  'promo.rules': 'A piece gets one promotion at most. If offers overlap, the till picks the combination that saves the customer the most. Buy X get Y reduces the cheapest pieces in each group.',
  'promo.err.name': 'Give it a name.',
  'promo.err.percent': 'The percent has to be between 1 and 99 (or 100 for free pieces).',
  'promo.err.buy': 'How many to buy: 1 to 20.',
  'promo.err.get': 'How many to get: 1 to 20.',
  'promo.err.targets': 'Choose at least one.',
  'promo.err.dates': 'The end has to be after the start.',
  'promo.err.started': 'It has already started, so it can only be stopped.',
  'approval.reasonDrawer': 'Opening the drawer without a sale',
  'legal.label': 'Terms and privacy',
  'legal.terms': 'Terms of service',
  'legal.privacy': 'Privacy',
  'chart.bestDay': 'Best day',
  'chart.busiestHour': 'Busiest hour',
  'chart.highest': 'Highest',
  'audit.what': 'What happened',
  'audit.details': 'Details',
  'checkout.heldAddedTo': 'The held sale was added to the sale already on the till.',
  'checkout.removed': 'Took off {name}',
  'checkout.undo': 'Undo',
  'print.title': 'Printing',
  'print.intro': 'Which printer each kind of printing goes to, for every till in this shop.',
  'print.how': 'How to print',
  'print.viaWindow': 'The print window',
  'print.viaHelper': 'By itself, through the print helper',
  'print.windowHow': 'The browser shows its print window every time and you press Print. Nothing to install. To print with no window, choose the print helper.',
  'print.checking': 'Checking...',
  'print.running': 'Print helper running {version}',
  'print.notRunningShort': 'Print helper not running on this PC',
  'print.oldHelper': 'This helper is too old to print - download the new one',
  'print.checkAgain': 'Check again',
  'print.installHow': 'Download the print helper, unzip it on the till PC and double-click "Start with Windows (hidden)" once. It runs with no window, starts with Windows and restarts itself if it stops. Then press Check again.',
  'print.download': 'Download the print helper',
  'print.receiptPrinter': 'Receipt printer',
  'print.receiptPrinterHint': 'Receipts, gift receipts, reprints, gift cards and the drawer.',
  'print.typePrinter': 'Windows would not list the printers. Type the printer\'s name exactly as Windows shows it.',
  'print.paper': 'Receipt paper',
  'print.labelPrinter': 'Label printer',
  'print.labelPrinterHint': 'Price labels, at the label size set on the Labels screen.',
  'print.labelsInWindow': 'Use the print window',
  'print.notOnThisPc': 'not on this PC',
  'print.testReceipt': 'Print a test receipt',
  'print.testLine': 'Test print',
  'print.testSent': 'Sent to the printer.',
  'print.a4Note': 'Reports and A4 pages still use the print window, so they can be saved as PDF.',
  'print.auto': 'Print the receipt after every sale',
  'print.autoHelper': 'Straight to the receipt printer, no window.',
  'print.autoWindow': 'The print window opens by itself; you still press Print. To skip it, print through the helper.',
  'print.drawer': 'There is a cash drawer',
  'print.drawerNote': 'Plugged into the receipt printer. Puts an Open drawer button on the till.',
  'print.drawerOnCash': 'Open it after every sale that took cash',
  'print.drawerOnCashWindow': 'Without the helper, the printer itself must be set to open the drawer when it prints (Windows: the printer\'s Printing preferences, Cash drawer).',
  'print.pin': 'Drawer pin',
  'print.pinHint': 'Most drawers use 2. If the test does nothing, try 5, then both.',
  'print.pinBoth': 'Both',
  'print.testDrawer': 'Open the drawer (test)',
  'print.drawerSent': 'Sent. The drawer should be open now.',
  'print.advanced': 'Advanced',
  'print.helperUrl': 'Print helper address on this PC',
  'print.helperUrlHint': 'Only if the helper was started on a different port.',
  'print.openDrawer': 'Open drawer',
  'print.notRunning': 'The print helper is not running on this PC. In its folder, double-click "Start with Windows (hidden)".',
  'print.failed': 'The printer did not print.',
  'print.fellBack': 'Printed through the print window instead: {why}',
  'move.waiting': 'This till is moving to {host}. {n} sale(s) still to upload first: keep it online and it moves by itself.',
  'error.moduleOff': "{name} is not part of this shop's system",
  'error.moduleOffHint': 'Contact your system provider to add it.',
  'pf.brand': 'System owner',
  'pf.signInTitle': 'Sign in to manage shops',
  'pf.navShops': 'Shops',
  'pf.navActivity': 'Activity',
  'pf.navAccount': 'My account',
  'pf.signOut': 'Sign out',
  'pf.shopsSub': 'Every shop on this server, each with its own database and staff.',
  'pf.newShop': 'New shop',
  'pf.search': 'Search by name, code or phone',
  'pf.kpiShops': 'Shops',
  'pf.kpiActive': 'Active',
  'pf.kpiPaused': 'Paused',
  'pf.kpiTakings': 'Sales, last 30 days',
  'pf.receipts': '{n} receipts',
  'pf.colShop': 'Shop',
  'pf.colStatus': 'Status',
  'pf.colModules': 'Modules',
  'pf.colStaff': 'Staff',
  'pf.colSales': 'Last 30 days',
  'pf.colLastSale': 'Last sale',
  'pf.colSize': 'Data',
  'pf.active': 'Active',
  'pf.suspended': 'Paused',
  'pf.notSetUp': 'Not set up yet',
  'pf.default': 'Default',
  'pf.defaultHint': 'Tills that type no account code sign in to this shop.',
  'pf.never': 'Never',
  'pf.noShops': 'No shops yet',
  'pf.noShopsHint': 'Create the first one, then hand its owner the sign-in details.',
  'pf.noMatch': 'No shop matches that search.',
  'pf.statsError': 'This shop\'s figures could not be read.',
  'pf.newSub': 'Creates the shop\'s own database, its first user, and the modules it gets.',
  'pf.secShop': 'The shop',
  'pf.shopName': 'Shop name',
  'pf.shopNameHint': 'Shown on its sign-in screen and receipts. The shop can change it later in Settings.',
  'pf.codeHint': 'What its staff type at sign-in. Lower-case letters, digits and dashes. It cannot be changed later.',
  'pf.codeFree': 'Available',
  'pf.codeTaken': 'Already used by another shop',
  'pf.codeBad': 'Use 2 to 32 lower-case letters, digits or dashes',
  'pf.contactName': 'Contact person',
  'pf.contactPhone': 'Phone',
  'pf.notes': 'Notes',
  'pf.notesHint': 'Only you see these - never the shop.',
  'pf.secOwner': 'The shop owner\'s sign-in',
  'pf.ownerName': 'Their name',
  'pf.generate': 'Generate',
  'pf.show': 'Show',
  'pf.hide': 'Hide',
  'pf.passwordHint': 'At least 8 characters. They can change it after signing in.',
  'pf.ownerLang': 'Their language',
  'pf.secModules': 'Modules',
  'pf.modulesHint': 'Selling, products, receiving stock, the Today screen, staff and settings are always included.',
  'pf.preset': 'Start from',
  'pf.modulesCount': '{n} of {total}',
  'pf.create': 'Create shop',
  'pf.createdTitle': 'Shop created',
  'pf.createdHint': 'Hand these to the shop owner. The password is not shown again.',
  'pf.address': 'Address',
  'pf.copy': 'Copy',
  'pf.copyAll': 'Copy sign-in details',
  'pf.copied': 'Copied',
  'pf.openShop': 'Open the shop\'s page',
  'pf.createAnother': 'Create another',
  'pf.allShops': 'All shops',
  'pf.pause': 'Pause shop',
  'pf.resume': 'Resume shop',
  'pf.pauseTitle': 'Pause {name}?',
  'pf.pauseMsg': 'Nobody at this shop can sign in or use the till until you resume it. Nothing is deleted.',
  'pf.pauseDefaultNote': 'This is the default shop, so tills that type no account code are turned away too.',
  'pf.paused': 'Shop paused',
  'pf.resumed': 'Shop resumed',
  'pf.kpiProducts': 'Products',
  'pf.activeCount': '{n} active',
  'pf.kpiTills': 'Tills',
  'pf.seen': 'seen {when}',
  'pf.since': 'since {date}',
  'pf.modulesSaved': 'Modules saved. The shop sees the change on its next action.',
  'pf.modulesOffTitle': 'Switch off {list}?',
  'pf.modulesOffMsg': 'Those screens disappear for everyone in the shop. Nothing is deleted: switching them back on brings everything back.',
  'pf.switchOff': 'Switch off',
  'pf.undo': 'Undo changes',
  'pf.colName': 'Name',
  'pf.colRole': 'Role',
  'pf.colLastLogin': 'Last sign-in',
  'pf.inactive': 'Inactive',
  'pf.resetPassword': 'New password',
  'pf.resetTitle': 'New password for {user}',
  'pf.resetMsg': 'Their old password stops working and they are signed out on every device.',
  'pf.resetDone': 'Password changed. Hand it over now: it is not shown again.',
  'pf.setPassword': 'Set password',
  'pf.secDetails': 'Contact & notes',
  'pf.secSignIn': 'How its staff sign in',
  'pf.secActivity': 'History',
  'pf.noActivity': 'Nothing yet.',
  'pf.activitySub': 'Everything done from this panel, and every sign-in to it.',
  'pf.system': 'The system',
  'pf.ev.login': 'Signed in',
  'pf.ev.login_failed': 'Failed sign-in',
  'pf.ev.password_changed': 'Changed their own password',
  'pf.ev.shop_created': 'Created the shop',
  'pf.ev.modules_changed': 'Changed modules',
  'pf.ev.shop_suspended': 'Paused the shop',
  'pf.ev.shop_resumed': 'Resumed the shop',
  'pf.ev.shop_password_reset': 'Gave someone a new password',
  'pf.ev.shop_details_changed': 'Edited contact & notes',
  'pf.added': 'On: {list}',
  'pf.removed': 'Off: {list}',
  'pf.signedInAs': 'Signed in as {user}',
  'pf.lastLogin': 'Last sign-in {when}',
  'pf.changePassword': 'Change password',
  'pf.currentPassword': 'Current password',
  'pf.newPassword': 'New password',
  'pf.confirmPassword': 'New password again',
  'pf.passwordMin': 'At least 10 characters.',
  'pf.mismatch': 'The two new passwords are not the same.',
  'pf.passwordChanged': 'Password changed. Any other device is signed out.',
  'pf.loadFailed': 'Could not load this page.',
} as const;

export type StringKey = keyof typeof en;
